const COUNTRY_TO_CODE: Record<string, string> = {
  Netherlands: 'NL',
  'The Netherlands': 'NL',
  Belgium: 'BE',
  Germany: 'DE',
  France: 'FR',
  'United Kingdom': 'GB',
  UK: 'GB',
  Spain: 'ES',
  Italy: 'IT',
  Portugal: 'PT',
  Austria: 'AT',
  Switzerland: 'CH',
  Luxembourg: 'LU',
  Denmark: 'DK',
  Sweden: 'SE',
  Norway: 'NO',
  Finland: 'FI',
  Ireland: 'IE',
  Poland: 'PL',
  'United States': 'US',
  USA: 'US',
  Canada: 'CA',
  'United Arab Emirates': 'AE',
  UAE: 'AE',
};

/**
 * Resolves a country name or ISO code to an uppercase ISO code.
 * Returns undefined when the value is missing or not a known country.
 */
export const resolveCountryCode = (country?: string): string | undefined => {
  const trimmed = country?.trim() ?? '';
  if (!trimmed) return undefined;

  const fromName = COUNTRY_TO_CODE[trimmed];
  if (fromName) return fromName;

  if (/^[a-z]{2}$/i.test(trimmed)) return trimmed.toUpperCase();

  return undefined;
};

export const getCountryCode = (country?: string): string =>
  resolveCountryCode(country) ?? 'NL';

/** ISO codes where display must match `getCountryCode` / CMS copy, not raw Intl. */
const COUNTRY_CODE_DISPLAY_NAME: Record<string, string> = {
  NL: 'The Netherlands',
};

let regionDisplayNames: Intl.DisplayNames | undefined;

const getRegionDisplayNames = (): Intl.DisplayNames => {
  regionDisplayNames ??= new Intl.DisplayNames('en', { type: 'region' });
  return regionDisplayNames;
};

/**
 * Resolves a Uniform / BFF ISO country code to an English display name.
 * Uniform `storeItem.country` is a code (e.g. NL), not a free-text label.
 */
export const getCountryNameFromCode = (countryCode?: string): string => {
  const trimmed = countryCode?.trim() ?? '';
  if (!trimmed) return '';

  const code = trimmed.toUpperCase();
  const override = COUNTRY_CODE_DISPLAY_NAME[code];
  if (override) return override;

  const fromIntl = getRegionDisplayNames().of(code);
  if (fromIntl && fromIntl.toUpperCase() !== code) {
    return fromIntl;
  }

  return trimmed;
};

export const getCountryFlag = (countryCode: string): string => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};
